import React, { useEffect, useRef, useState } from "react";
import { PageContentApiP1Response } from "../../../../../../../../api/services/pageContent/models";
import {
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiTextField,
} from "../../../../../../../../components";
import { MuiDoubleLabelWithChip } from "../../../../../../../../components/doubleLabelWithChip/MuiDoubleLabelWithChip";
import {
  MAX_LENGTH_100,
  SELECTION_TYPE,
} from "../../../../../../../../helpers/constants";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { ParticipantAnswer } from "../../../../../../modelsAndSchemas/Models";
import {
  mapPersonaQuestions,
  QuestionWithSelectedAnswers,
} from "../../NextInfoQuestion.util";
import { AboutParticipantWrapper } from "./AboutParticipant.style";
import { useFetchReferenceDataMutation } from "../../../../../../../../api/services/references/referencesApi";

interface AboutParticipantProps {
  pageContentData?: PageContentApiP1Response;
  aboutParticipantNext?: ParticipantAnswer[];
  nextPersona?: string;
  preferredLanguage?: string;
  isViewProgram?: boolean;
}

const AboutParticipantItem: React.FC<{
  question: QuestionWithSelectedAnswers;
  isViewProgram: boolean;
}> = ({ question, isViewProgram }) => {
  const { commonARIALabel } = useNavigationSiteCore();
  const { commonLabels } = useSiteCore();
  const singleValue = question.selectedAnswers[0]?.AnswerText ?? "--";
  const modalityRef = useRef<HTMLDivElement>(null);

  const multiValues = question.selectedAnswers.map((a) => ({
    value: a.AnswerText,
    label: a.AnswerText,
  }));

  const isMultiSelect =
    question.QuestionDesignType === SELECTION_TYPE.multiSelect;

  const renderSingleValue = () =>
    isViewProgram ? (
      <MuiDoubleLabel
        label={question.QuestionTitle}
        replaceEmptyWithDash
        value={singleValue}
      />
    ) : (
      <MuiBox className="about-textfield">
        <MuiTextField
          label={question.QuestionTitle}
          value={singleValue}
          placeholder={commonLabels.inputPlaceHolder}
          disabled
          maxLength={MAX_LENGTH_100}
          requiredARIALabel={commonARIALabel.required}
        />
      </MuiBox>
    );

  return (
    <AboutParticipantWrapper>
      <MuiBox className="about-participant">
        <MuiGrid key={question.QuestionId}>
          {isMultiSelect ? (
            <MuiDoubleLabelWithChip
              label={question.QuestionTitle}
              replaceEmptyWithDash={multiValues.length === 0}
              value={multiValues}
              chipPopoverGridRef={modalityRef}
              chipPopoverHeaderLabel={question.QuestionTitle}
              showMoreARIALabel={commonARIALabel.showNMore}
              removeChipNameARIALabel={commonARIALabel.removeChipName}
              closePopoverARIALabel={commonARIALabel.closePopover}
              isDialogTitleWithEllipsis
            />
          ) : (
            renderSingleValue()
          )}
        </MuiGrid>
      </MuiBox>
    </AboutParticipantWrapper>
  );
};

export const AboutParticipant: React.FC<AboutParticipantProps> = ({
  pageContentData,
  aboutParticipantNext = [],
  nextPersona = "",
  preferredLanguage = "en-US",
  isViewProgram = false,
}) => {
  const { mutateAsync: fetchReferenceData } = useFetchReferenceDataMutation();
  const [questions, setQuestions] = useState<QuestionWithSelectedAnswers[]>([]);

  useEffect(() => {
    const loadQuestions = async () => {
      if (!pageContentData || aboutParticipantNext.length === 0) {
        setQuestions([]);
        return;
      }

      const loadedQuestions = await mapPersonaQuestions(
        nextPersona,
        pageContentData,
        aboutParticipantNext,
        preferredLanguage,
        fetchReferenceData,
      );
      setQuestions(loadedQuestions);
    };

    loadQuestions();
  }, [pageContentData, aboutParticipantNext, nextPersona, preferredLanguage]);

  return (
    <>
      {questions.map((question) => (
        <AboutParticipantItem
          key={question.QuestionId}
          question={question}
          isViewProgram={isViewProgram}
        />
      ))}
    </>
  );
};
